if (!customElements.get('products-toolbar')) {
  class ProductsToolbar extends HTMLElement {
    constructor() {
      super();
      this.init();
    }

    init() {
      this.filtersComponent = document.querySelector('facet-filters');
      this.filtersColumn = document.querySelector('.main-products-grid__filters');
      /* The grid/list switcher moved OUT of this component on 2026-09-17 — it now sits on
         the results-count row just above the products, because up here in the toolbar the
         customer could press it and see nothing change (owner's report). So look inside
         first, for any template that still nests it, then fall back to the document.
         Without the fallback the listener is never bound and the control is inert. */
      this.layoutSwitcher =
        this.querySelector('.products-toolbar__layout') ||
        document.querySelector('.products-toolbar__layout');
      this.compareToggle = document.getElementById('compare-switch');
      this.sortBy = this.querySelector('.products-toolbar__sort');
      this.productsGrid = document.querySelector('.main-products-grid');

      if (this.filtersColumn) {
        this.filterToggle = this.querySelector('.js-toggle-filters');
        this.filterToggle.addEventListener('click', this.toggleFilters.bind(this));
      }

      if (this.layoutSwitcher) {
        this.layoutSwitcher.addEventListener('change', this.handleLayoutChange.bind(this));

        // Restore the layout the customer last chose.
        //
        // This used to be gated on `theme.mediaMatches.md` (>= 769px) because the toggle was hidden
        // on mobile and mobile was always grid. The owner reversed that on 2026-09-16: the toggle is
        // now shown on mobile too (the `display:none` in llm-meilisearch-collection.css went with
        // this gate — they must always move together, or the control appears and then silently
        // forgets what it was told, which reads as a broken setting).
        //
        // Grid is still the DEFAULT everywhere; this only restores an explicit choice. The key is
        // that storageUtil only holds a value once someone has actually pressed the toggle, so
        // "has a stored value" means "chose deliberately" and never "never thought about it".
        // ⚠️ RESTORES 'grid' AS WELL AS 'list' — changed 2026-10-20, and it is load-bearing now.
        //
        // The old test was `layout === 'list'`, which was harmless only because GRID WAS ALWAYS THE
        // SERVER DEFAULT: restoring grid would have changed nothing. That assumption died when
        // collections gained `custom.default_layout` (the spec list-view pilot, task #81) — a
        // collection can now arrive from the server already in list.
        //
        // With the old test, a customer on such a collection who deliberately pressed GRID got list
        // back on every reload: their stored 'grid' matched nothing, so nothing overrode the
        // server. The explicit choice was silently discarded — precisely the "control appears and
        // then forgets what it was told, which reads as a broken setting" failure the note above
        // warns about.
        //
        // Precedence now actually implemented: customer choice > collection default > grid.
        //
        // ⚠️ THE CHOICE IS REMEMBERED PER COLLECTION, not site-wide (owner, 2026-10-23: pressing list
        // on a list collection made the next grid collection open in list too — "the default view is
        // always shown first"). One global key meant a choice made on one page overrode every other
        // collection's default. Now the stored choice only applies back on the page it was made on.
        // The old global key is dropped so a stale site-wide choice stops overriding defaults.
        try { theme.storageUtil.remove('products-layout'); } catch (e) { /* storage blocked */ }
        const layout = theme.storageUtil.get(ProductsToolbar.layoutKey());
        if (layout === 'list' || layout === 'grid') {
          const toggle = document.getElementById(`${layout}-layout-opt`);
          if (toggle) toggle.click();
          this.handleLayoutChange(null, layout);
        }
      }

      if (this.compareToggle) {
        this.compareToggle.addEventListener('change', this.toggleCompare.bind(this));

        const isChecked = theme.storageUtil.get('compare-toggle', true);
        if (isChecked !== null) {
          this.toggleCompare(null, isChecked, true);
        } else {
          this.toggleCompare(null, this.compareToggle.checked, false);
        }

        setTimeout(() => {
          this.classList.add('transition-ready');
          this.productsGrid.classList.add('transition-ready');
        }, 500);
      } else if (theme.settings.compareToggle === 'none') {
        this.toggleCompare(null, true, false);
      }

      if (!this.filtersComponent && this.sortBy) {
        this.sortBy.addEventListener('change', ProductsToolbar.handleSortByChange);
      }
    }

    /**
     * Updates the UI to reflect the compare state
     * @param {object} evt - Change event object
     * @param {boolean} checked - Whether compare is enabled or not (optional)
     * @param {boolean} persist - Whether to persist the new state (optional)
     */
    toggleCompare(evt, checked = null, persist = true) {
      const isChecked = evt ? evt.target.checked : checked;
      this.productsGrid.dataset.compare = isChecked;

      const compareButton = document.getElementById('compare-drawer-open');
      compareButton.classList.toggle('is-out', !isChecked);

      if (!evt && this.compareToggle) this.compareToggle.checked = isChecked;

      if (persist && !Shopify.designMode) {
        theme.storageUtil.set('compare-toggle', isChecked);
      }

      // Load scripts/styles if not already loaded
      if (isChecked) {
        const resourcesTag = document.getElementById('compare-resources');
        if (resourcesTag) {
          const resources = JSON.parse(resourcesTag.textContent);
          resources.forEach((resource) => {
            if (resource.type === 'js') {
              const script = document.createElement('script');
              script.src = resource.path;
              document.head.appendChild(script);
            } else if (resource.type === 'css') {
              const link = document.createElement('link');
              link.href = resource.path;
              link.type = 'text/css';
              link.rel = 'stylesheet';
              document.head.appendChild(link);
            }
          });
          resourcesTag.remove();
        }
      }
    }

    /**
     * Toggles open/closed state of the filters on desktop.
     */
    toggleFilters() {
      this.filterToggle.classList.toggle('is-active');
      this.filtersOpen = this.filterToggle.classList.contains('is-active');
      this.filterToggle.setAttribute('aria-expanded', this.filtersOpen);

      if (theme.mediaMatches.lg) {
        this.filtersColumn.classList.toggle('lg:js-hidden', !this.filtersOpen);
        const productsList = document.querySelector('.main-products-grid__results > .grid');

        productsList.className = this.filtersOpen
          ? productsList.dataset.filtersOpenClasses
          : productsList.dataset.filtersClosedClasses;
      } else {
        this.filtersComponent.open();
      }
    }

    /**
     * Handles 'change' events on the layout switcher buttons.
     * @param {object} evt - Event object.
     * @param {string} layout - 'grid' or 'list' - the layout to change to.
     */
    handleLayoutChange(evt, layout) {
      this.productsGrid.dataset.layout = layout || evt.target.value;
      if (!layout) theme.storageUtil.set(ProductsToolbar.layoutKey(), evt.target.value);
    }

    /**
     * Storage key for the layout choice on THIS page: one per collection (tag-filtered paths such as
     * /collections/gloves/latex-free share their collection's key), one for search, etc.
     * @returns {string}
     */
    static layoutKey() {
      const path = window.location.pathname.replace(/\/+$/, '');
      const coll = path.match(/\/collections\/[^/]+/);
      return `products-layout:${coll ? coll[0] : path || '/'}`;
    }

    /**
     * Handles when a sort by dropdown is changed (and filtering is disabled)
     * @param {object} evt - Event object.
     */
    static handleSortByChange(evt) {
      const urlObj = new URL(window.location.href);
      urlObj.searchParams.set('sort_by', evt.detail.selectedValue);
      urlObj.hash = 'products-toolbar';
      window.location.href = urlObj.toString();
    }
  }

  customElements.define('products-toolbar', ProductsToolbar);
}
